/**
 * 策略跟单卡片（列表 / 详情）共用：法币兑换限额 + 余额封顶的原始区间，以及列表同款展示/校验边界（按 10 取整等）。
 */

import { isAicopyUsdt, isAicopyUsdc } from "./aicopyCurrencies";
import { isAicopyVoucherOrderOption } from "./aicopyOrderOptions";

/** 法币合并兑换限额，可买上限按可用余额封顶（与 getAicopyEffectiveMinMax 一致） */
function parseNonNegativeNumber(value) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** 1 USDT = rate 法币；Amount range 取 fiatRate 与 convert exchangeRate 的有效值（较大者），与下限一致 */
function resolveAicopyFiatToUsdtPolicyRate(fiatRate, convertConfig) {
  const rates = [fiatRate, convertConfig?.exchangeRate]
    .map((v) => parseNonNegativeNumber(v))
    .filter((r) => r > 0);
  return rates.length ? Math.max(...rates) : 1;
}

/**
 * 法币→USDT：preview 到账 targetAmount = (fiat/rate)*(1-percentFee) - fixedFee（手续费币种 USDT）
 * 反推满足策略最低 USDT 门槛所需的法币下限。
 */
export function computeMinFiatForMinUsdtAfterConvertFee(minUsdt, fiatRate, convertConfig) {
  const min = parseNonNegativeNumber(minUsdt);
  if (min <= 0) return 0;
  const rate = resolveAicopyFiatToUsdtPolicyRate(fiatRate, convertConfig);
  const percentFee = Math.min(parseNonNegativeNumber(convertConfig?.percentFee), 0.999999);
  const fixedFee = parseNonNegativeNumber(convertConfig?.fixedFee);
  const feeFactor = Math.max(1e-12, 1 - percentFee);
  return ((min + fixedFee) * rate) / feeFactor;
}

/** 法币→USDT：反推到账不超过策略 USDT 上限所需的法币上限 */
export function computeMaxFiatForMaxUsdtAfterConvertFee(maxUsdt, fiatRate, convertConfig) {
  const max = parseNonNegativeNumber(maxUsdt);
  if (max <= 0) return 0;
  const rate = resolveAicopyFiatToUsdtPolicyRate(fiatRate, convertConfig);
  const percentFee = Math.min(parseNonNegativeNumber(convertConfig?.percentFee), 0.999999);
  const fixedFee = parseNonNegativeNumber(convertConfig?.fixedFee);
  const feeFactor = Math.max(1e-12, 1 - percentFee);
  return ((max + fixedFee) * rate) / feeFactor;
}

/**
 * USDC→USDT（convertType 7 / R7）：exchangeRate 为 1 USDC 可换 USDT 数量（与 Convert swap 一致）。
 * grossUsdt = usdc × rate；netUsdt = grossUsdt × (1 − percentFee) − fixedFee（手续费币种 USDT）。
 */
function resolveUsdcToUsdtExchangeRate(convertConfig) {
  const rate = parseNonNegativeNumber(convertConfig?.exchangeRate);
  return rate > 0 ? rate : 1;
}

/** R7：反推满足策略最低 USDT 所需的 USDC 下限 */
export function computeMinUsdcForMinUsdtAfterR7Fee(minUsdt, convertConfig) {
  const min = parseNonNegativeNumber(minUsdt);
  if (min <= 0) return 0;
  const rate = resolveUsdcToUsdtExchangeRate(convertConfig);
  const percentFee = Math.min(parseNonNegativeNumber(convertConfig?.percentFee), 0.999999);
  const fixedFee = parseNonNegativeNumber(convertConfig?.fixedFee);
  const feeFactor = Math.max(1e-12, 1 - percentFee);
  return (min + fixedFee) / (rate * feeFactor);
}

/** R7：反推到账不超过策略 USDT 上限所需的 USDC 上限 */
export function computeMaxUsdcForMaxUsdtAfterR7Fee(maxUsdt, convertConfig) {
  const max = parseNonNegativeNumber(maxUsdt);
  if (max <= 0) return 0;
  const rate = resolveUsdcToUsdtExchangeRate(convertConfig);
  const percentFee = Math.min(parseNonNegativeNumber(convertConfig?.percentFee), 0.999999);
  const fixedFee = parseNonNegativeNumber(convertConfig?.fixedFee);
  const feeFactor = Math.max(1e-12, 1 - percentFee);
  return (max + fixedFee) / (rate * feeFactor);
}

function applyConvertMaxAmountCap(amount, convertConfig) {
  if (convertConfig?.maxAmount == null) return amount;
  const convertMax = parseNonNegativeNumber(convertConfig.maxAmount);
  return convertMax > 0 ? Math.min(amount, convertMax) : amount;
}

function applyAicopyUsdcEffectiveMinMax({ minUsdt, maxUsdt, convertConfig }) {
  if (convertConfig) {
    let min = computeMinUsdcForMinUsdtAfterR7Fee(minUsdt, convertConfig);
    if (convertConfig.minAmount != null) {
      min = Math.max(min, parseNonNegativeNumber(convertConfig.minAmount));
    }
    const policyMax = maxUsdt > 0 ? computeMaxUsdcForMaxUsdtAfterR7Fee(maxUsdt, convertConfig) : 0;
    return {
      min,
      maxForSliderCap: policyMax,
      validationMaxCap: applyConvertMaxAmountCap(policyMax, convertConfig),
    };
  }
  return { min: minUsdt, maxForSliderCap: maxUsdt, validationMaxCap: maxUsdt };
}

function applyAicopyFiatEffectiveMinMax({ minUsdt, maxUsdt, rate, convertConfig }) {
  const policyRate = resolveAicopyFiatToUsdtPolicyRate(rate, convertConfig);
  if (convertConfig) {
    let min = computeMinFiatForMinUsdtAfterConvertFee(minUsdt, rate, convertConfig);
    if (convertConfig.minAmount != null) {
      min = Math.max(min, parseNonNegativeNumber(convertConfig.minAmount));
    }
    const policyMax = maxUsdt > 0 ? computeMaxFiatForMaxUsdtAfterConvertFee(maxUsdt, rate, convertConfig) : 0;
    return {
      min,
      maxForSliderCap: policyMax,
      validationMaxCap: applyConvertMaxAmountCap(policyMax, convertConfig),
    };
  }
  const max = maxUsdt * policyRate;
  return { min: minUsdt * policyRate, maxForSliderCap: max, validationMaxCap: max };
}

/** preview/下单前：到账 USDT 是否达到策略最低门槛 */
export function isAicopyFiatConvertTargetSufficient(previewData, minUsdt) {
  const target = parseFloat(String(previewData?.targetAmount ?? "").replace(/,/g, ""));
  const min = parseNonNegativeNumber(minUsdt);
  if (!Number.isFinite(target) || min <= 0) return false;
  return target + 1e-8 >= min;
}

export function getAicopyEffectiveMinMax({ minUsdt, maxUsdt, currency, rate, convertConfig, currentAvailableNum }) {
  const c = isAicopyVoucherOrderOption(currency) ? "USDT" : currency;
  let min;
  let maxForSliderCap;
  let validationMaxCap;

  if (isAicopyUsdt(c)) {
    min = minUsdt;
    maxForSliderCap = maxUsdt;
    validationMaxCap = maxUsdt;
  } else if (isAicopyUsdc(c)) {
    ({ min, maxForSliderCap, validationMaxCap } = applyAicopyUsdcEffectiveMinMax({ minUsdt, maxUsdt, convertConfig }));
  } else {
    ({ min, maxForSliderCap, validationMaxCap } = applyAicopyFiatEffectiveMinMax({ minUsdt, maxUsdt, rate, convertConfig }));
  }

  const capByBalance = currentAvailableNum > 0 ? currentAvailableNum : Infinity;
  const effectiveMax = Math.min(validationMaxCap, capByBalance);
  return { effectiveMin: min, effectiveMax, maxForSliderCap, validationMaxCap };
}

function ceilAmountToTwoDecimalsStr(effectiveMin) {
  if (!Number.isFinite(effectiveMin) || effectiveMin <= 0) return "0.00";
  return (Math.ceil((effectiveMin + Number.EPSILON) * 100) / 100).toFixed(2);
}

function resolveAicopyStrategyCardBounds(effectiveMin, maxCap) {
  const preciseMin = parseFloat(ceilAmountToTwoDecimalsStr(effectiveMin));
  const flooredPolicyMax =
    Number.isFinite(maxCap) && maxCap > 0
      ? Math.floor((maxCap + Number.EPSILON) * 100) / 100
      : maxCap;
  const amountRangeLabelMax =
    Number.isFinite(preciseMin) && Number.isFinite(flooredPolicyMax) ? Math.max(flooredPolicyMax, preciseMin) : flooredPolicyMax;

  let minIn;
  let maxIn;
  if (Number.isFinite(preciseMin) && Number.isFinite(amountRangeLabelMax)) {
    const byTenMin = Math.ceil(preciseMin / 10) * 10;
    const byTenMax = Math.floor(amountRangeLabelMax / 10) * 10;
    if (byTenMin <= byTenMax) {
      minIn = byTenMin;
      maxIn = byTenMax;
    } else {
      minIn = preciseMin;
      maxIn = amountRangeLabelMax >= preciseMin ? amountRangeLabelMax : flooredPolicyMax;
    }
  } else {
    minIn = preciseMin;
    maxIn = amountRangeLabelMax;
  }

  const maxInCurrentForSlider =
    Number.isFinite(maxIn) && maxIn > 0 ? Math.min(maxCap, maxIn) : maxCap;

  return { minIn, maxIn, maxInCurrentForSlider };
}

/**
 * Strategy 列表卡：minIn / maxInForValidation 供 Amount range 文案、输入校验与滑块（含 convert maxAmount）。
 */
export function computeAicopyStrategyCardAmountBounds({ effectiveMin, maxForSliderCap, validationMaxCap }) {
  const display = resolveAicopyStrategyCardBounds(effectiveMin, maxForSliderCap);
  const validationCap = validationMaxCap != null ? validationMaxCap : maxForSliderCap;
  const validation = resolveAicopyStrategyCardBounds(effectiveMin, validationCap);
  return {
    minIn: display.minIn,
    maxIn: display.maxIn,
    maxInForValidation: validation.maxIn,
    maxInCurrentForSlider: validation.maxInCurrentForSlider,
  };
}

/** 余额是否达到最小下单金额（与滑块/跟单前「余额不足」判断一致） */
export function isAicopyBalanceSufficientForDefaultAmount(currentAvailableNum, minIn) {
  if (!Number.isFinite(minIn) || minIn <= 0) return false;
  if (!Number.isFinite(currentAvailableNum) || currentAvailableNum <= 0) return false;
  return currentAvailableNum + 1e-8 >= minIn;
}
